import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';

const PLAN_LABELS = {
  starter: 'Starter (30-Day Free Trial)',
  pro: 'Pro ($49/mo)',
};

const PostPaymentRegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams?.get('plan') || 'starter';
  const sessionId = searchParams?.get('session_id') || '';

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    if (form?.password !== form?.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form?.password?.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email: form?.email,
        password: form?.password,
        options: {
          data: {
            full_name: form?.fullName,
            business_name: form?.businessName,
            role: 'personal_trainer',
            plan: planId,
            stripe_session_id: sessionId,
          },
        },
      });

      if (signUpError) throw signUpError;

      navigate(`/registration-confirmation?plan=${planId}&name=${encodeURIComponent(form?.fullName)}`);
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b border-border/40 bg-card/60 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-7 h-7 wellness-gradient-primary rounded-lg">
              <Icon name="Heart" size={14} color="white" />
            </div>
            <span className="font-bold text-foreground">Form Forge</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="CheckCircle" size={15} className="text-primary" />
            <span>
              {planId === 'starter' ? 'Trial activated' : 'Payment confirmed'} — complete your account
            </span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Success banner */}
            <div className="bg-primary/10 border border-primary/30 rounded-xl px-4 py-3 mb-6 flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircle" size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {planId === 'starter' ? 'Free trial ready!' : 'Payment successful!'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Plan: <span className="text-primary font-medium">{PLAN_LABELS?.[planId]}</span>
                </p>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-foreground mb-1">Create your account</h1>
            <p className="text-sm text-muted-foreground mb-7">
              Set up your trainer profile to get started.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={form?.fullName}
                  onChange={handleChange}
                  required
                  placeholder="Jane Smith"
                  className="w-full bg-muted/50 border border-border/60 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Business / Studio Name <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={form?.businessName}
                  onChange={handleChange}
                  placeholder="Elite Performance Studio"
                  className="w-full bg-muted/50 border border-border/60 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={form?.email}
                  onChange={handleChange}
                  required
                  placeholder="jane@example.com"
                  className="w-full bg-muted/50 border border-border/60 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={form?.password}
                    onChange={handleChange}
                    required
                    placeholder="Min. 8 characters"
                    className="w-full bg-muted/50 border border-border/60 rounded-xl px-4 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={15} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={form?.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Repeat password"
                  className="w-full bg-muted/50 border border-border/60 rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                />
              </div>

              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl px-4 py-3 text-sm text-destructive flex items-center space-x-2">
                  <Icon name="AlertCircle" size={14} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full wellness-gradient-primary text-white py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-60 hover:opacity-90 shadow-lg mt-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Create Account & Go to Dashboard</span>
                    <Icon name="ArrowRight" size={15} />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-xs text-muted-foreground mt-5">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-primary hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PostPaymentRegisterPage;
